import { useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmData } from '../../contexts/CrmDataContext';
import { formatSupabaseError } from '../../lib/formatSupabaseError';
import { postProjectUpdate } from '../../lib/projectUpdates';
import { projectUpdateAuthorId } from '../../utils/ownerId';

interface ProjectListReplyBoxProps {
  projectId: string;
  onPosted: () => void;
}

export function ProjectListReplyBox({ projectId, onPosted }: ProjectListReplyBoxProps) {
  const { profile } = useAuth();
  const { data } = useCrmData();
  const [message, setMessage] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authorId = useMemo(() => {
    try {
      return projectUpdateAuthorId(profile, data.users);
    } catch {
      return '';
    }
  }, [profile, data.users]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (posting || !message.trim() || !authorId) return;

    setPosting(true);
    setError(null);
    try {
      await postProjectUpdate({ projectId, authorId, message });
      setMessage('');
      onPosted();
    } catch (err) {
      setError(formatSupabaseError(err, 'crm'));
    } finally {
      setPosting(false);
    }
  }

  return (
    <form className="project-list-reply" onSubmit={(e) => void handleSubmit(e)} onClick={(e) => e.stopPropagation()}>
      {error && <p className="project-list-reply__error">{error}</p>}
      {!authorId && (
        <p className="project-list-reply__hint">Sign in again if Reply remains disabled.</p>
      )}
      <div className="project-list-reply__row">
        <input
          className="field-input project-list-reply__input"
          type="text"
          placeholder="Enter management reply"
          value={message}
          disabled={posting || !authorId}
          onChange={(e) => setMessage(e.target.value)}
        />
        <button
          type="submit"
          className="btn-primary btn-sm project-list-reply__btn"
          disabled={posting || !message.trim() || !authorId}
        >
          {posting ? '…' : 'Reply'}
        </button>
      </div>
    </form>
  );
}
